import { ApiError, NetworkError } from '@/api/client';
import type { TranslateFn } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/dictionaries/en';
import type { SkuImportRow, SkuImportStatus } from '@/api/types';

/**
 * Products (SKU) import from Excel - the wording side. The server decides every
 * outcome; this file only turns its codes into one plain sentence. A raw code,
 * id or request id is never shown to the user - an unknown code reads as a
 * generic sentence.
 */

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
/** Mirrors the server's own limit (512 KiB) so an oversized file is refused before it is uploaded. */
export const MAX_FILE_BYTES = 512 * 1024;

/** Saves a base64 payload as a file in the browser. */
export function downloadBase64(filename: string, base64: string, mime: string): void {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const x = String(r.result ?? '');
      resolve(x.slice(x.indexOf(',') + 1));
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

const FILE_REASON_KEYS: Record<string, TranslationKey> = {
  NOT_XLSX: 'skuImport.file.notXlsx',
  TOO_LARGE: 'skuImport.file.tooLarge',
  MACROS: 'skuImport.file.macros',
  EXTERNAL_LINKS: 'skuImport.file.externalLinks',
  EMBEDDED_OBJECTS: 'skuImport.file.embedded',
  CORRUPT: 'skuImport.file.corrupt',
  TOO_MANY_ROWS: 'skuImport.file.tooManyRows',
  HEADER_NOT_FOUND: 'skuImport.file.headerNotFound',
  EMPTY: 'skuImport.file.empty',
  FORMULA: 'skuImport.file.formula',
  NO_SHEET: 'skuImport.file.noSheet',
  ALREADY_USED: 'skuImport.file.alreadyUsed',
};

const CODE_KEYS: Record<string, TranslationKey> = {
  'IMPORT.HAS_ERRORS': 'skuImport.err.hasErrors',
  'IMPORT.DRY_RUN_STALE': 'skuImport.err.stale',
  'IMPORT.COMMIT_PARTIAL': 'skuImport.err.partial',
  'IMPORT.OWNER_NOT_ALLOWED': 'skuImport.err.ownerNotAllowed',
  'IMPORT.OWNER_REQUIRED': 'skuImport.err.ownerRequired',
};

/** After these the check no longer matches the catalogue: the user has to upload the file again. */
export const RESTART_CODES: ReadonlySet<string> = new Set(['IMPORT.HAS_ERRORS', 'IMPORT.DRY_RUN_STALE', 'IMPORT.COMMIT_PARTIAL', 'IMPORT.FILE_INVALID']);

export function describeSkuImportError(t: TranslateFn, err: unknown): string {
  if (err instanceof NetworkError) return t('skuImport.err.network');
  if (!(err instanceof ApiError)) return t('skuImport.err.generic');
  if (err.status === 429) return t('skuImport.err.tooMany');
  const p = err.params as Record<string, string | number | undefined>;
  if (err.code === 'IMPORT.FILE_INVALID') {
    const key = FILE_REASON_KEYS[String(p.reason ?? '')] ?? 'skuImport.file.corrupt';
    return t(key, { max: p.max ?? 500, row: p.row ?? '' });
  }
  const key = CODE_KEYS[err.code];
  return key ? t(key) : t('skuImport.err.generic');
}

const FIELD_KEYS: Record<string, TranslationKey> = {
  'sku.code': 'skuImport.row.code',
  'sku.name': 'skuImport.row.name',
  'sku.weightGrams': 'skuImport.row.weight',
  'sku.lengthMm': 'skuImport.row.length',
  'sku.widthMm': 'skuImport.row.width',
  'sku.heightMm': 'skuImport.row.height',
  'sku.barcode': 'skuImport.row.barcode',
  'sku.variantAttrs.size': 'skuImport.row.size',
  'sku.variantAttrs.color': 'skuImport.row.color',
  'sku.variantAttrs.article': 'skuImport.row.code',
  'sku.variant': 'skuImport.row.variant',
};

/** The sentence under a row; null when the row has no problem to explain. */
export function describeSkuRow(t: TranslateFn, row: SkuImportRow): string | null {
  if (row.status !== 'ERROR') return null;
  const code = row.error?.code ?? '';
  if (code === 'IMPORT.CONFLICT.DUPLICATE_ROW_KEY') return t('skuImport.row.duplicateKey');
  if (code === 'IMPORT.CONFLICT.DUPLICATE_BARCODE') return t('skuImport.row.duplicateBarcode');
  if (code === 'IMPORT.ROW_INVALID') {
    const key = FIELD_KEYS[row.error?.params?.field ?? ''];
    if (key) return t(key);
  }
  return t('skuImport.row.generic');
}

export const STATUS_TONE: Record<SkuImportStatus, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  NEW: 'ok',
  UPDATE: 'warn',
  SKIP: 'neutral',
  ERROR: 'danger',
};

export const STATUS_KEYS: Record<SkuImportStatus, TranslationKey> = {
  NEW: 'skuImport.status.NEW',
  UPDATE: 'skuImport.status.UPDATE',
  SKIP: 'skuImport.status.SKIP',
  ERROR: 'skuImport.status.ERROR',
};

/** Rows with a problem first (the user must act on them), then the sheet order. */
export function sortRowsErrorsFirst(rows: readonly SkuImportRow[]): SkuImportRow[] {
  return [...rows].sort((a, b) => Number(b.status === 'ERROR') - Number(a.status === 'ERROR') || a.rowNumber - b.rowNumber);
}

/** Client-side pre-check so an unusable file is refused before it is uploaded (the server still checks everything). */
export function precheckSkuFile(file: File): ApiError | null {
  const reason = !file.name.toLowerCase().endsWith('.xlsx') ? 'NOT_XLSX' : file.size > MAX_FILE_BYTES ? 'TOO_LARGE' : null;
  return reason ? new ApiError(400, { code: 'IMPORT.FILE_INVALID', params: { reason }, request_id: '-' }) : null;
}
